import React, { useEffect, useState } from 'react';
import StaffScanGate from '@/components/StaffScanGate';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Loader2, Package, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const RefinishBundleManager = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emitting, setEmitting] = useState(null);
  const [tab, setTab] = useState('candidates');

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        apiClient.get('/sales-return/refinish-candidates'),
        apiClient.get('/sales-return/refinish-bundles?limit=200'),
      ]);
      setGroups(a.data.groups || []);
      setBundles(b.data.bundles || []);
    } catch (err) {
      toast.error('Failed to load refinish data');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const emit = async (group) => {
    setEmitting(group.sku_code);
    try {
      const res = await apiClient.post('/sales-return/refinish-bundles', {
        sku_code: group.sku_code,
        design_name: group.design_name,
        shade: group.shade,
        size: group.size,
        usn_codes: group.usns,
      });
      toast.success(`Refinish bundle ${res.data.bundle_id} created (${res.data.usn_count} USNs) — queued for SFG Put-Away`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create bundle');
    } finally { setEmitting(null); }
  };

  return (
    <StaffScanGate stationName="Sales Return — Refinish Bundles">
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="refinish-bundle-manager">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/sales-return/bundle-receipt')}
              className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Refinish Bundle Manager</h1>
              <p className="text-sm text-[#E8DCC8]">Group same-SKU REFINISH USNs and queue the bundle for SFG Put-Away</p>
            </div>
          </div>
          <Button variant="secondary" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="candidates" data-testid="tab-candidates">
              Candidates ({groups.length})
            </TabsTrigger>
            <TabsTrigger value="bundles" data-testid="tab-bundles">
              Emitted Bundles ({bundles.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="candidates">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Pending REFINISH USNs grouped by SKU</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU</TableHead>
                      <TableHead>Design</TableHead>
                      <TableHead>Shade</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>USN Count</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!groups.length && (
                      <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8">
                        No REFINISH USNs awaiting bundling.
                      </TableCell></TableRow>
                    )}
                    {groups.map((g) => (
                      <TableRow key={`${g.sku_code}-${g.shade}-${g.size}`}
                        data-testid={`refinish-group-${g.sku_code}`}>
                        <TableCell className="font-mono text-xs">{g.sku_code}</TableCell>
                        <TableCell className="text-xs">{g.design_name || '—'}</TableCell>
                        <TableCell className="text-xs">{g.shade || '—'}</TableCell>
                        <TableCell className="text-xs">{g.size || '—'}</TableCell>
                        <TableCell><Badge>{g.usn_count}</Badge></TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" disabled={emitting === g.sku_code}
                            onClick={() => emit(g)}
                            data-testid={`emit-bundle-${g.sku_code}`}
                            className="bg-[#2D5F4F] hover:bg-[#234A3D]">
                            {emitting === g.sku_code
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                              : <Package className="w-3.5 h-3.5 mr-1" />}
                            Emit Bundle
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="bundles">
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bundle ID</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>USN Count</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!bundles.length && (
                      <TableRow><TableCell colSpan={5} className="text-center text-gray-400 py-8">
                        No refinish bundles emitted yet.
                      </TableCell></TableRow>
                    )}
                    {bundles.map((b) => (
                      <TableRow key={b.bundle_id}>
                        <TableCell className="font-mono text-xs">{b.bundle_id}</TableCell>
                        <TableCell className="text-xs">{b.sku_code}</TableCell>
                        <TableCell><Badge variant="outline">{b.usn_count}</Badge></TableCell>
                        <TableCell><Badge>{b.status}</Badge></TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {new Date(b.created_at).toLocaleString('en-IN')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
    </StaffScanGate>
  );
};

export default RefinishBundleManager;
